/**
 * QuizHistoryOrchestrator
 *
 * Coordinates between attemptRepository (Supabase) and localStorage fallback
 * for unified quiz/exam history operations.
 *
 * Implements singleton pattern for global state management.
 * Provides pub/sub mechanism for React component integration.
 *
 * Features:
 * - Unified API for history operations
 * - Automatic localStorage fallback when not authenticated
 * - Migration from localStorage to database on sign-in
 * - State change notifications for React
 */

import { attemptRepository } from '../../repositories/attemptRepository';

const LOCAL_STORAGE_KEY = 'quiz_exam_history_v1';
const MIGRATION_FLAG_KEY = 'quiz_history_migrated_to_db';

class QuizHistoryOrchestrator {
  constructor() {
    // Singleton enforcement
    if (QuizHistoryOrchestrator._instance) {
      return QuizHistoryOrchestrator._instance;
    }
    QuizHistoryOrchestrator._instance = this;

    // Internal state
    this._isInitialized = false;
    this._isAuthenticated = false;
    this._userId = null;
    this._subscribers = new Set();
    this._lastError = null;
    this._localAttempts = [];
    this._dbAttempts = [];
    this._isLoading = false;
  }

  /**
   * Initialize the orchestrator
   * @param {boolean} isAuthenticated - Whether user is authenticated
   * @param {string|null} userId - The Auth0 user ID (user.sub)
   * @returns {Promise<{ success: boolean, count: number, error?: string }>}
   */
  async initialize(isAuthenticated, userId) {
    try {
      this._isLoading = true;
      this._isAuthenticated = isAuthenticated;
      this._userId = userId;
      this._notifySubscribers();

      if (isAuthenticated && userId) {
        // Load from database
        const attempts = await attemptRepository.getAllHistory(userId);
        this._dbAttempts = attempts || [];

        // Check for pending migration
        await this._migrateLocalAttemptsIfNeeded();

        this._isInitialized = true;
        this._isLoading = false;
        this._notifySubscribers();

        return {
          success: true,
          count: this._dbAttempts.length
        };
      } else {
        // Load from localStorage
        this._loadLocalAttempts();
        this._isInitialized = true;
        this._isLoading = false;
        this._notifySubscribers();

        return {
          success: true,
          count: this._localAttempts.length
        };
      }
    } catch (error) {
      console.error('[QuizHistoryOrchestrator] Initialize error:', error);
      this._lastError = error.message;
      this._isInitialized = true;
      this._isLoading = false;

      // Fallback to localStorage
      this._loadLocalAttempts();
      this._notifySubscribers();

      return {
        success: false,
        count: this._localAttempts.length,
        error: error.message
      };
    }
  }

  /**
   * Switch storage mode (called on login/logout)
   * @param {boolean} isAuthenticated - Whether user is authenticated
   * @param {string|null} userId - The Auth0 user ID
   */
  async switchStorageMode(isAuthenticated, userId) {
    this._isAuthenticated = isAuthenticated;
    this._userId = userId;
    this._isLoading = true;
    this._notifySubscribers();

    if (isAuthenticated && userId) {
      const attempts = await attemptRepository.getAllHistory(userId);
      this._dbAttempts = attempts || [];
      await this._migrateLocalAttemptsIfNeeded();
    } else {
      this._dbAttempts = [];
      this._loadLocalAttempts();
    }

    this._isLoading = false;
    this._notifySubscribers();
  }

  /**
   * Save a new attempt
   * @param {Object} attemptData - The attempt data to save
   * @returns {Promise<{ success: boolean, attempt?: Object, error?: string }>}
   */
  async saveAttempt(attemptData) {
    try {
      if (this._isAuthenticated && this._userId) {
        // Save to database
        const savedAttempt = await attemptRepository.saveAttempt({
          ...attemptData,
          auth0_id: this._userId
        });

        // Add to local cache
        this._dbAttempts = [savedAttempt, ...this._dbAttempts];
        this._notifySubscribers();

        return {
          success: true,
          attempt: savedAttempt
        };
      } else {
        // Save to localStorage
        const localAttempt = {
          id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          ...attemptData,
          completed_at: attemptData.completed_at || new Date().toISOString()
        };

        this._localAttempts = [localAttempt, ...this._localAttempts];
        this._saveLocalAttempts();
        this._notifySubscribers();

        return {
          success: true,
          attempt: localAttempt
        };
      }
    } catch (error) {
      console.error('[QuizHistoryOrchestrator] Save attempt error:', error);

      // Fallback to localStorage on database error
      if (this._isAuthenticated) {
        const localAttempt = {
          id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          ...attemptData,
          completed_at: attemptData.completed_at || new Date().toISOString(),
          _pendingSync: true
        };
        this._localAttempts = [localAttempt, ...this._localAttempts];
        this._saveLocalAttempts();
        this._notifySubscribers();
      }

      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get all attempts (combined from DB and local)
   * @returns {Array}
   */
  getAllAttempts() {
    if (this._isAuthenticated && this._userId) {
      return this._dbAttempts;
    }
    return this._localAttempts;
  }

  /**
   * Get attempts by type (quiz or exam)
   * @param {string} contentType - 'quiz' or 'exam'
   * @returns {Array}
   */
  getAttemptsByType(contentType) {
    const attempts = this.getAllAttempts();
    return attempts.filter(a => a.content_type === contentType);
  }

  /**
   * Get attempts for specific content
   * @param {string} contentType - 'quiz' or 'exam'
   * @param {string} contentId - The content ID
   * @returns {Array}
   */
  getAttemptsByContent(contentType, contentId) {
    const attempts = this.getAllAttempts();
    return attempts.filter(a => 
      a.content_type === contentType && 
      String(a.content_id) === String(contentId)
    );
  }

  /**
   * Get the latest attempt for specific content
   * @param {string} contentType - 'quiz' or 'exam'
   * @param {string} contentId - The content ID
   * @returns {Object|null}
   */
  getLatestAttempt(contentType, contentId) {
    const attempts = this.getAttemptsByContent(contentType, contentId);
    return attempts[0] || null;
  }

  /**
   * Get history formatted as a map by content_id (latest attempt per content)
   * @param {string} contentType - Optional filter by 'quiz' or 'exam'
   * @returns {Object}
   */
  getHistoryMap(contentType = null) {
    const attempts = contentType 
      ? this.getAttemptsByType(contentType)
      : this.getAllAttempts();

    const historyMap = {};
    attempts.forEach((attempt) => {
      // Only keep the most recent attempt per content (attempts are sorted desc)
      if (!historyMap[attempt.content_id]) {
        historyMap[attempt.content_id] = {
          attemptId: attempt.id,
          score: attempt.score,
          total: attempt.total_questions,
          percentage: attempt.is_adaptive ? attempt.adaptive_confidence : attempt.percentage,
          completedAt: attempt.completed_at,
          mode: attempt.mode,
          isNGN: attempt.is_ngn,
          isAdaptive: attempt.is_adaptive,
          result: attempt.adaptive_result,
          answers: attempt.answers_data,
          questionsAdministered: attempt.questions_administered,
        };
      }
    });

    return historyMap;
  }

  /**
   * Get statistics
   * @returns {{ totalAttempts: number, examsCompleted: number, quizzesCompleted: number, avgScore: number }}
   */
  getStats() {
    const attempts = this.getAllAttempts();
    
    const stats = {
      totalAttempts: attempts.length,
      examsCompleted: attempts.filter(a => a.content_type === 'exam').length,
      quizzesCompleted: attempts.filter(a => a.content_type === 'quiz').length,
      avgScore: 0
    };

    if (attempts.length > 0) {
      const totalPercentage = attempts.reduce((sum, a) => {
        const pct = a.is_adaptive ? (a.adaptive_confidence || 0) : (a.percentage || 0);
        return sum + pct;
      }, 0);
      stats.avgScore = Math.round(totalPercentage / attempts.length);
    }

    return stats;
  }

  /**
   * Subscribe to state changes
   * @param {Function} callback - Function to call on state change
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    this._subscribers.add(callback);

    return () => {
      this._subscribers.delete(callback);
    };
  }

  /**
   * Get current state
   * @returns {Object}
   */
  getState() {
    return {
      isInitialized: this._isInitialized,
      isAuthenticated: this._isAuthenticated,
      isLoading: this._isLoading,
      attempts: this.getAllAttempts(),
      count: this.getAllAttempts().length,
      lastError: this._lastError
    };
  }

  /**
   * Load attempts from localStorage
   * @private
   */
  _loadLocalAttempts() {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this._localAttempts = Array.isArray(parsed) ? parsed : [];
        // Sort by completed_at descending
        this._localAttempts.sort((a, b) => 
          new Date(b.completed_at) - new Date(a.completed_at)
        );
      } else {
        this._localAttempts = [];
      }
    } catch (e) {
      console.warn('[QuizHistoryOrchestrator] Failed to load local attempts:', e);
      this._localAttempts = [];
    }
  }

  /**
   * Save attempts to localStorage
   * @private
   */
  _saveLocalAttempts() {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this._localAttempts));
    } catch (e) {
      console.warn('[QuizHistoryOrchestrator] Failed to save local attempts:', e);
    }
  }

  /**
   * Migrate local attempts to database if needed
   * @private
   */
  async _migrateLocalAttemptsIfNeeded() {
    try {
      // Check if already migrated
      const migrationKey = `${MIGRATION_FLAG_KEY}_${this._userId}`;
      const alreadyMigrated = localStorage.getItem(migrationKey);

      if (alreadyMigrated) {
        return;
      }

      // Load local attempts
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!stored) {
        localStorage.setItem(migrationKey, 'true');
        return;
      }

      const localAttempts = JSON.parse(stored);
      if (!Array.isArray(localAttempts) || localAttempts.length === 0) {
        localStorage.setItem(migrationKey, 'true');
        return;
      }

      console.log(`[QuizHistoryOrchestrator] Migrating ${localAttempts.length} attempts to database`);

      let migratedCount = 0;
      for (const attempt of localAttempts) {
        try {
          // Skip if it's already a DB attempt (has UUID format ID)
          if (attempt.id && !attempt.id.startsWith('local_')) {
            continue;
          }

          await attemptRepository.saveAttempt({
            auth0_id: this._userId,
            content_type: attempt.content_type,
            content_id: attempt.content_id,
            content_title: attempt.content_title,
            mode: attempt.mode,
            score: attempt.score,
            total_questions: attempt.total_questions,
            percentage: attempt.percentage,
            is_adaptive: attempt.is_adaptive || false,
            adaptive_result: attempt.adaptive_result || null,
            adaptive_theta: attempt.adaptive_theta || null,
            adaptive_confidence: attempt.adaptive_confidence || null,
            is_ngn: attempt.is_ngn || false,
            answers_data: attempt.answers_data,
            questions_administered: attempt.questions_administered || null,
            time_spent_seconds: attempt.time_spent_seconds || 0,
            completed_at: attempt.completed_at,
          });
          migratedCount++;
        } catch (err) {
          console.warn('[QuizHistoryOrchestrator] Failed to migrate attempt:', err);
        }
      }

      // Reload from database to get all migrated attempts
      if (migratedCount > 0) {
        const freshAttempts = await attemptRepository.getAllHistory(this._userId);
        this._dbAttempts = freshAttempts || [];
      }

      localStorage.setItem(migrationKey, 'true');
      console.log(`[QuizHistoryOrchestrator] Migration complete: ${migratedCount} attempts synced`);
    } catch (error) {
      console.error('[QuizHistoryOrchestrator] Migration error:', error);
    }
  }

  /**
   * Notify all subscribers of state change
   * @private
   */
  _notifySubscribers() {
    const state = this.getState();
    this._subscribers.forEach(callback => {
      try {
        callback(state);
      } catch (error) {
        console.error('[QuizHistoryOrchestrator] Subscriber error:', error);
      }
    });
  }

  /**
   * Get singleton instance
   * @static
   * @returns {QuizHistoryOrchestrator}
   */
  static getInstance() {
    if (!QuizHistoryOrchestrator._instance) {
      new QuizHistoryOrchestrator();
    }
    return QuizHistoryOrchestrator._instance;
  }

  /**
   * Reset singleton (for testing)
   * @static
   */
  static resetInstance() {
    if (QuizHistoryOrchestrator._instance) {
      QuizHistoryOrchestrator._instance._subscribers.clear();
      QuizHistoryOrchestrator._instance = null;
    }
  }
}

// Singleton instance
QuizHistoryOrchestrator._instance = null;

export default QuizHistoryOrchestrator;
export { QuizHistoryOrchestrator };
